import { api, requireAccount, withAccount } from "./core";

// billingApi — galaxy-scoped (Stripe customer/subscription is keyed off
// account_id). The BE handlers all read account_id from context so the
// FE must pass account_login.
export interface BillingInfo {
  plan: string;
  status: string;
  seats?: number; // billable seats = org member count (1 seat = 1 GitHub account)
  // True when the sub is active but set to end at current_period_end (Stripe
  // cancel_at_period_end) — lets the UI show "ending <date>" instead of normal
  // ongoing billing. current_period_end is the effective-until date (RFC3339).
  cancel_at_period_end?: boolean;
  current_period_end?: string;
  // Stripe billing cadence ('month' | 'year'); '' / undefined for free. The only
  // signal that distinguishes pro-monthly from pro-yearly (the plan field folds
  // both to "pro"), so the Plan screen shows 月払い/年払い from this.
  billing_interval?: string;
}

// SeatMember mirrors the backend usecase.SeatMember — a member of the account
// plus whether they occupy a paid seat. Admins always read billable=true.
export interface SeatMember {
  user_id: number;
  login: string;
  avatar_url: string;
  role: string;
  billable: boolean;
}

// Paged<T> mirrors the backend {items,total,page,page_size} envelope (same
// shape as HQ's paged endpoints). `items` may arrive as Go's nil slice (JSON
// null), so callers coerce it to [].
export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

// SeatRosterPage is the seat-management envelope: one page of the roster plus
// the account-wide seat counts the member page always shows — seats_purchased
// (paid seats bought; 0 = free/no-sub), seats_used (members holding a seat),
// member_count (total members). The gap (used vs purchased) is what the
// "buy more seats" CTA keys on.
export interface SeatRosterPage extends Paged<SeatMember> {
  seats_purchased: number;
  seats_used: number;
  member_count: number;
}

// planApi — the galaxy's entitlement plan (account.plan projection), the
// single source the plan badge reads. Distinct from billingApi (the Stripe
// subscription): this is what limits/quotas/rate-limit also see, so badge ==
// entitlement and a dev plan override reflects immediately.
export interface PlanInfo {
  plan: string;
  max_repo_size_kb: number; // free-tier repo size cap; 0 = unlimited (paid)
  max_repos?: number; // PRIVATE-repo count cap (public repos uncapped); 0 = unlimited
  account_type?: string; // "personal" | "org"
  galaxy_mode?: "public" | "private" | "empty"; // drives plan eligibility (public org → flat pro)
  // Metered private-repo overage (Pro only, docs/design/metered-private-repos.md).
  // included_private_repos > 0 = metered mode: private repos beyond this are billed
  // at repo_overage_usd/mo each. Both 0 on free/nova (free is hard-capped via
  // max_repos; nova meters per-seat).
  included_private_repos?: number;
  repo_overage_usd?: number;
}
export const planApi = {
  get: (accountLogin: string) =>
    api.get<PlanInfo>(withAccount("/plan", requireAccount(accountLogin, "planApi.get"))),
  // BATCH plan projection for the per-galaxy badges (switcher / control room),
  // replacing N× get(). One request for every login; the BE returns plans only
  // for galaxies the viewer is a member of (others omitted → caller defaults free).
  getMany: (accountLogins: string[]) => {
    const p = new URLSearchParams();
    p.set("account_logins", accountLogins.join(","));
    return api.get<{ plans: Record<string, string> }>(`/plans?${p}`);
  },
};

export const billingApi = {
  get: (accountLogin: string) =>
    api.get<BillingInfo>(withAccount("/billing", requireAccount(accountLogin, "billingApi.get"))),
  sync: (accountLogin: string) =>
    api.post<BillingInfo>(withAccount("/billing/sync", requireAccount(accountLogin, "billingApi.sync")), {}),
  checkout: (accountLogin: string, plan: string, locale?: string) =>
    api.post<{ checkout_url: string }>(withAccount("/billing/checkout", requireAccount(accountLogin, "billingApi.checkout")), { plan, locale }),
  // seats returns one page of the seat roster ({items,total,page,page_size}).
  // The Members page drives page/pageSize; items is coerced to [] (Go nil
  // slice → JSON null).
  seats: (accountLogin: string, params?: { page?: number; pageSize?: number }) =>
    api
      .get<SeatRosterPage>(
        withAccount("/billing/seats", requireAccount(accountLogin, "billingApi.seats"), {
          ...(params?.page != null ? { page: String(params.page) } : {}),
          ...(params?.pageSize != null ? { page_size: String(params.pageSize) } : {}),
        }),
      )
      .then((r) => ({ ...r, items: r.items ?? [] })),
  setSeatBillable: (accountLogin: string, userId: number, billable: boolean) =>
    api.patch<{ billable: boolean }>(withAccount(`/billing/seats/${userId}`, requireAccount(accountLogin, "billingApi.setSeatBillable")), { billable }),
  // setPurchasedSeats changes how many paid seats the org bought (buy more /
  // reduce) on the active Nova sub — prorated on Stripe. Throws ApiError with
  // code "seats_below_assigned" (409) when reducing below the assigned count.
  setPurchasedSeats: (accountLogin: string, seats: number) =>
    api.put<{ seats_purchased: number; seats_used: number }>(
      withAccount("/billing/seats", requireAccount(accountLogin, "billingApi.setPurchasedSeats")),
      { seats },
    ),
  // portal returns a Stripe customer-portal URL (cancel / resume / payment
  // method / invoices). Throws ApiError (400) when there's no Stripe customer
  // yet (free / never-checked-out).
  portal: (accountLogin: string) =>
    api.post<{ url: string }>(withAccount("/billing/portal", requireAccount(accountLogin, "billingApi.portal")), {}),
};
